import { ehConcluido } from './statusDoChamado';

/**
 * Valores de filtro que se resolvem em QUEM está olhando.
 *
 * ## O problema
 *
 * O filtro guarda o id do operador escolhido. Um filtro salvo e
 * compartilhado chamado "Meus abertos" mostra, para todo mundo, os chamados
 * de quem o criou — o que torna filtro de equipe inútil.
 *
 * "Agente logado" é um valor que significa coisas diferentes para cada pessoa
 * que aplica o filtro. Quem o resolve é o SERVIDOR, na hora de aplicar (ver
 * `FilterService`) — a tela só sabe oferecê-lo e exibi-lo.
 */

/**
 * O marcador, igual ao `FilterService::VALOR_AGENTE_LOGADO` da api.
 *
 * Os dois precisam bater. Mudar um sem o outro **não dá erro em lugar
 * nenhum**: o filtro simplesmente deixa de casar e passa a não mostrar nada,
 * que é o pior jeito de quebrar — parece que não há chamados.
 */
export const VALOR_AGENTE_LOGADO = '__agente_logado__';

/**
 * O grupo de atendimento de quem está olhando.
 *
 * O campo "Grupo de atendimento" filtra pelo grupo do RESPONSÁVEL, então
 * este valor resolve para o grupo do usuário autenticado — é o que faz um
 * filtro "pendentes do meu setor" servir ao setor de cada um.
 */
export const VALOR_GRUPO_DO_AGENTE = '__grupo_do_agente_logado__';

/**
 * Chamado sem responsável.
 *
 * Não é dinâmico como os outros dois — não depende de quem olha. Entra pelo
 * mesmo mecanismo porque é o mesmo gesto para quem usa: uma escolha visível
 * na lista. Hoje só dá para achar esses chamados pelo operador "é nulo", que
 * fica escondido onde ninguém procura.
 */
export const VALOR_SEM_RESPONSAVEL = '__sem_responsavel__';

/**
 * Chamado não concluído: tudo que não é Resolvido, Fechado ou Cancelado.
 *
 * Como o "sem responsável", não depende de quem olha. Resolve outra dor:
 * hoje, para montar "o que está em aberto", a pessoa escolhe na mão todos os
 * status que não são finais — e um status novo cadastrado depois **não entra
 * nos filtros já salvos**, que passam a esconder chamados sem avisar. Este
 * valor inverte a pergunta, e a lista de finais é curta e nomeada.
 */
export const VALOR_STATUS_NAO_CONCLUIDO = '__status_nao_concluido__';

export const ROTULOS = {
    [VALOR_AGENTE_LOGADO]: 'Agente logado (quem abrir o filtro)',
    [VALOR_GRUPO_DO_AGENTE]: 'Grupo do agente logado',
    [VALOR_SEM_RESPONSAVEL]: 'Sem responsável',
    [VALOR_STATUS_NAO_CONCLUIDO]: 'Não concluído',
};

/** Mantido: o primeiro valor dinâmico, e o mais usado. */
export const ROTULO_AGENTE_LOGADO = ROTULOS[VALOR_AGENTE_LOGADO];

export const ehAgenteLogado = (valor) => valor === VALOR_AGENTE_LOGADO;

export const ehValorDinamico = (valor) => Object.prototype.hasOwnProperty.call(
    ROTULOS,
    valor,
);

/**
 * Quais valores cada campo aceita.
 *
 * Por CAMPO, e não uma lista só, porque nem todo valor faz sentido em todo
 * lugar:
 *
 * - `created_by` não aceita "Sem responsável": a coluna é NOT NULL, não
 *   existe chamado sem criador, e a opção seria uma escolha que nunca
 *   devolve nada;
 * - `service_group_id` só aceita o grupo: "Agente logado" ali compararia uma
 *   pessoa com um departamento;
 * - `status_id` só aceita "Não concluído", que é uma pergunta sobre status e
 *   não sobre pessoas.
 *
 * Oferecer o que o servidor não sabe resolver tem um sintoma ruim: filtro
 * vazio, sem explicação nenhuma.
 */
export const VALORES_POR_CAMPO = {
    assigned_to: [VALOR_AGENTE_LOGADO, VALOR_SEM_RESPONSAVEL],
    created_by: [VALOR_AGENTE_LOGADO],
    service_group_id: [VALOR_GRUPO_DO_AGENTE],
    status_id: [VALOR_STATUS_NAO_CONCLUIDO],
};

export const valoresDinamicosDe = (fieldName) => VALORES_POR_CAMPO[fieldName] || [];

export const aceitaValorDinamico = (fieldName) => valoresDinamicosDe(fieldName).length > 0;

/** Compatibilidade com quem só pergunta pelo agente logado. */
export const aceitaAgenteLogado = (fieldName) =>
    valoresDinamicosDe(fieldName).includes(VALOR_AGENTE_LOGADO);

/**
 * A lista de opções do campo, com os valores dinâmicos na FRENTE.
 *
 * Pseudo-opções, com o marcador no lugar do id: assim o `<Autocomplete>`
 * trata todas do mesmo jeito — escolher, exibir a escolhida e devolver o
 * valor — sem um caminho especial que precisaria ser lembrado em cada um
 * desses três lugares.
 *
 * Na frente porque são as escolhas que tornam o filtro compartilhável;
 * enterradas no meio de uma lista de nomes, ninguém as encontra.
 */
export const opcoesComDinamicos = (fieldName, itens) => [
    ...valoresDinamicosDe(fieldName).map((valor) => ({ id: valor, name: ROTULOS[valor] })),
    ...(Array.isArray(itens) ? itens : []),
];

/** Mantido para o seletor de operadores, que foi o primeiro a usar isto. */
export const opcoesDeOperador = (users) => opcoesComDinamicos('assigned_to', users);

/**
 * O texto que representa um valor na lista de condições.
 *
 * A lista pode não conter mais o item (operador desligado, departamento
 * removido), e nesse caso o valor cru é melhor que um branco: pelo menos diz
 * que há uma condição ali.
 */
export const rotuloDoValor = (valor, itens) => {
    if (ehValorDinamico(valor)) return ROTULOS[valor];

    const item = (Array.isArray(itens) ? itens : []).find((x) => String(x?.id) === String(valor));

    return item ? (item.name || item.description) : valor;
};

/** Mantido: mesma coisa, nome antigo. */
export const rotuloDoOperador = (valor, users) => rotuloDoValor(valor, users);

/**
 * Avalia um valor dinâmico contra um chamado, NO NAVEGADOR.
 *
 * ## Por que isto existe se o servidor já resolve
 *
 * Porque a mesma condição é avaliada em DOIS lugares. O servidor aplica o
 * filtro na consulta — é ele que pagina. A tela reavalia as condições sobre
 * as linhas já carregadas, para a busca e a mesclagem.
 *
 * Não ensinar o marcador à segunda avaliação dá o pior desfecho possível: o
 * servidor devolve as linhas certas e a tela descarta todas — "nenhum ticket
 * encontrado" com o filtro funcionando perfeitamente por baixo. Foi
 * exatamente o que aconteceu, e só apareceu no teste em tela: a comparação
 * caía no ramo de TEXTO (`isNaN('__agente_logado__')` é verdadeiro) e
 * comparava o marcador com o NOME do responsável.
 *
 * @param {object}   args
 * @param {number[]} [args.finalizados]  ids dos status finais, obrigatório
 *   apenas para `VALOR_STATUS_NAO_CONCLUIDO`.
 * @returns {boolean|null} `null` quando o valor não é dinâmico — e aí quem
 *   chamou segue pela comparação normal.
 */
export const avaliarValorDinamico = ({ campo, valor, ticket, usuarioAtual, finalizados }) => {
    if (!ehValorDinamico(valor)) return null;

    if (valor === VALOR_SEM_RESPONSAVEL) {
        return ticket?.assigned_to?.id == null;
    }

    if (valor === VALOR_STATUS_NAO_CONCLUIDO) {
        // `ehConcluido` já trata os casos degenerados: sem a lista de ids ele
        // decide pelo NOME do status, e sem nenhum dos dois devolve `false`
        // -- ou seja, "não concluído", que é a resposta da api no mesmo caso
        // (`aplicarStatusNaoConcluido` devolve `1 = 1` sem status finais).
        //
        // **Bater com o servidor é o requisito**, não um detalhe: esta função
        // reavalia no navegador as mesmas linhas que o servidor selecionou.
        // Ser mais restritivo aqui dá o defeito mais caro desta tela -- as
        // linhas certas chegam, a tela descarta todas, e aparece "nenhum
        // ticket encontrado" sem erro em lugar nenhum.
        return !ehConcluido(ticket, finalizados);
    }

    if (valor === VALOR_GRUPO_DO_AGENTE) {
        const meu = usuarioAtual?.service_group_id;
        const dele = ticket?.assigned_to?.service_group_id
            ?? ticket?.assigned_to?.service_group?.id
            ?? null;

        if (meu == null || dele == null) return false;

        return Number(dele) === Number(meu);
    }

    // VALOR_AGENTE_LOGADO: compara com o campo que a condição nomeia --
    // `assigned_to` e `created_by` aceitam o mesmo marcador.
    if (usuarioAtual?.id == null) return false;

    const doChamado = campo === 'created_by'
        ? ticket?.created_by?.id
        : ticket?.assigned_to?.id;

    return doChamado != null && Number(doChamado) === Number(usuarioAtual.id);
};
